import { AIAgentId } from '../enum';

export interface ClarifyingQuestion {
  question: string;
  context: string;
}

export interface TAIAgentResponse {
  agentId?: string;
  content?: string;
  response?: string;
  userFriendlyExplanation?: string;
  summary?: string;
  results?: any[];
  fallbackUsed?: boolean;
  fallbackReason?: string;
  clarifyingQuestions?: ClarifyingQuestion[];
  [key: string]: any;
}

export interface HandleTAIResponseParams {
  data: TAIAgentResponse;
  handleNavigationAgentResponse?: (data: any) => Promise<string>;
}

/**
 * Handle different TAI agent response formats and extract the display text
 *
 * @param data - The response data from the TAI agent
 * @param handleNavigationAgentResponse - Optional handler for navigation-agent responses
 * @returns The display text to show to the user
 */
export async function handleTAIResponse({
  data,
  handleNavigationAgentResponse,
}: HandleTAIResponseParams): Promise<string> {
  let displayText: string | undefined;

  // Handle fallback case when navigation-agent failed
  if (data?.fallbackUsed && data?.fallbackReason) {
    console.log('[TAI Response] Fallback detected:', data.fallbackReason);
    return "I cannot locate that place. Please try to specify your request more clearly.";
  }

  // Handle navigation-agent response
  if (data?.agentId === AIAgentId.NavigationAgent && handleNavigationAgentResponse) {
    displayText = await handleNavigationAgentResponse(data);
  }
  // Handle default-agent response
  else if (data?.agentId === AIAgentId.DefaultAgent && typeof data?.content === 'string' && data.content.trim()) {
    displayText = data.content;
  }
  // Handle pdf-agent response
  else if (data?.agentId === AIAgentId.PdfAgent && typeof data?.response === 'string' && data.response.trim()) {
    displayText = data.response;
  }
  // Fallback to other possible fields
  else {
    displayText = (typeof data?.userFriendlyExplanation === 'string' && data.userFriendlyExplanation.trim())
      ? data.userFriendlyExplanation
      : (typeof data?.summary === 'string' && data.summary.trim())
        ? data.summary
        : (typeof data?.response === 'string' && data.response.trim())
          ? data.response
          : undefined;
  }

  return displayText ?? JSON.stringify(data ?? {}, null, 2);
}

